import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useParams, useSearchParams } from "react-router-dom"
import { useGet } from "utils/useFetch"
import { useAuthStore } from "stores"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { toast } from "react-toastify"
import { IShiftSwapsRes } from "../../typings"
import ShiftSwapModal from "../../components/ShiftSwapModal"
import {
  toggleIds,
  formatTime as formatTimeReadable,
} from "../utils/timesheet_utils"

const EMPTY_IMAGE =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/52d2cc1e-6777-43ce-8838-393f18a2f4c1.png"

const INITIAL_PAGE = { currentPage: 1, pageSize: 10 }

const ShiftSwapSection: React.FC = () => {
  const { x } = useParams()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore((state) => state)
  const queryClient = useQueryClient()

  const dutyLocationIdRaw = searchParams.get("dutyLocationId")
  const dutyLocationId = dutyLocationIdRaw ? Number(dutyLocationIdRaw) : null

  const [shiftSwapPage, setShiftSwapPage] = useState(INITIAL_PAGE)
  const [selectedShiftRow, setSelectedShiftRow] = useState<number[]>([])

  // ─── Data fetching ──────────────────────────────────────────────────────

  const {
    data: shiftSwapData,
    isLoading: isLoadingShiftSwaps,
    isPreviousData: isChangingShiftSwapPage,
  } = useGet<IShiftSwapsRes>({
    url: `/v1/time-attendance/shift-swaps?workplaceId=${x}&companyId=${
      user?.userCompanyId
    }${dutyLocationId ? `&dutyLocationId=${dutyLocationId}` : ""}&page=${
      shiftSwapPage.currentPage
    }&limit=${shiftSwapPage.pageSize}`,
    instanceKey: "ewaAxiosInstance",
    keepPreviousData: true,
  })

  // ─── Query invalidation ─────────────────────────────────────────────────

  const invalidateShiftSwaps = () =>
    queryClient.invalidateQueries({
      queryKey: [
        `/v1/time-attendance/shift-swaps?workplaceId=${x}&companyId=${
          user?.userCompanyId
        }${dutyLocationId ? `&dutyLocationId=${dutyLocationId}` : ""}&page=${
          shiftSwapPage.currentPage
        }&limit=${shiftSwapPage.pageSize}`,
      ],
    })

  // ─── Mutations ──────────────────────────────────────────────────────────

  const { mutate: approveShiftSwap, isLoading: isApprovingShiftSwap } =
    useMutation({
      mutationFn: ({ shiftSwapIds }: { shiftSwapIds: number[] }) =>
        ewaAxiosInstance.post(`/v1/time-attendance/shift-swaps/approve`, {
          shiftSwapIds,
        }),
      onSuccess: () => {
        invalidateShiftSwaps()
        setSelectedShiftRow([])
        toast.success("Shift swap approved successfully")
      },
      onError: onQueryError,
    })

  const { mutate: rejectShiftSwap, isLoading: isRejectingShiftSwap } =
    useMutation({
      mutationFn: ({ shiftSwapIds }: { shiftSwapIds: number[] }) =>
        ewaAxiosInstance.post(`/v1/time-attendance/shift-swaps/reject`, {
          shiftSwapIds,
        }),
      onSuccess: () => {
        invalidateShiftSwaps()
        setSelectedShiftRow([])
        toast.success("Shift swap rejected successfully")
      },
      onError: onQueryError,
    })

  // ─── Row selection ──────────────────────────────────────────────────────

  const toggleShiftSelectAll = () =>
    setSelectedShiftRow((prev) =>
      toggleIds(
        prev,
        (shiftSwapData?.data?.docs ?? []).map((d) => d.id)
      )
    )

  const handleShiftSwapRow = (id: number) =>
    setSelectedShiftRow((prev) => toggleIds(prev, [id]))

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="w-full">
      <ShiftSwapModal
        isApprovingShiftSwap={isApprovingShiftSwap}
        isRejectingShiftSwap={isRejectingShiftSwap}
        selectedShiftRow={selectedShiftRow}
        shiftSwapDocs={shiftSwapData}
        isLoading={isLoadingShiftSwaps || isChangingShiftSwapPage}
        emptyImage={EMPTY_IMAGE}
        page={shiftSwapPage}
        onPageChange={(page, pageSize) =>
          setShiftSwapPage({ currentPage: page, pageSize })
        }
        onApprove={(ids) => approveShiftSwap({ shiftSwapIds: ids })}
        onReject={(ids) => rejectShiftSwap({ shiftSwapIds: ids })}
        onToggleSelectAll={toggleShiftSelectAll}
        onRowSelect={handleShiftSwapRow}
        formatTime={formatTimeReadable}
      />
    </div>
  )
}

export default ShiftSwapSection
